import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Alert, Card, Col, Row, Table, ButtonGroup, Button } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import {
  StudentAvatar, AttendanceButtons, useIsMobile,
  isInactiveStudent, isTransferredStudent, DAY_NAMES, formatTimeShort,
} from "./guidanceShared";
import { leaveReportedByLabel } from "../utils/leaveSource";

const API = process.env.REACT_APP_API;

// ป้ายกำกับเมื่อมีการแจ้งลานักเรียนไว้ล่วงหน้า (ครูประจำชั้น / ฝ่ายปกครอง / หัวหน้าวิชาการ)
const LeaveBadge = ({ leave }) => {
  if (!leave) return null;
  // ใบกลับบ้านที่ยามกดว่ากลับเข้าโรงเรียนแล้ว ไม่นับเป็นลาของคาบนี้ แต่ยังขึ้นป้ายบอกที่มา
  const back = leave.returned_at ? ` · กลับเข้า ${leave.returned_at.slice(11, 16)} น.` : "";
  const badgeClass = `badge ${leave.is_active ? "bg-info" : "bg-secondary"} ms-2`;
  if (leave.source === "home_leave") {
    return (
      <span className={badgeClass} title={leave.leave_note || ""}>
        <i className="ri-home-heart-line me-1"></i>กลับบ้าน{leave.leave_note ? ` (${leave.leave_note})` : ""}{back}
      </span>
    );
  }
  const label = leave.leave_type === "sick" ? "ป่วย" : "กิจ";
  const by = leaveReportedByLabel(leave.source);
  return (
    <span className={badgeClass} title={leave.leave_note || ""}>
      <i className="ri-calendar-close-line me-1"></i>{by} ({label})
    </span>
  );
};

// เช็คชื่อคาบแนะแนว — pattern เดียวกับ teaching/CheckAttendance.js
// แต่ผูกกับ guid_id (คาบแนะแนวของห้อง) แทน teach_id
export default function GuidanceCheck() {
  const { guid_id } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState(null);
  const effectiveView = viewMode ?? (isMobile ? "card" : "table");

  const navigate = useNavigate();

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (skin === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [guidance, setGuidance] = useState(null); // slot + room context
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [topic, setTopic] = useState("");
  const [alreadyChecked, setAlreadyChecked] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // กันกดปุ่มบันทึกรัว ๆ

  // key สำหรับ draft ใน localStorage
  const attendanceKey = `guidance_${guid_id}_${selectedDate}`;

  const getSavedAttendance = React.useCallback(() => {
    const saved = localStorage.getItem(attendanceKey);
    return saved ? JSON.parse(saved) : null;
  }, [attendanceKey]);

  // ─── Load guidance slot (room context) ─────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${API}/getGuidanceById/${guid_id}`, {
      headers: { authorization: `Bearer ${getToken()}` },
    })
      .then((res) => setGuidance(res.data))
      .catch((err) => {
        Swal.fire({
          title: err.response?.data?.title || "เกิดข้อผิดพลาด",
          text: err.response?.data?.message || "ไม่สามารถโหลดข้อมูลคาบแนะแนวได้",
          icon: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [guid_id, setLoading]);

  // ─── Load students of the room ─────────────────────────────────────────────
  useEffect(() => {
    if (!guidance?.r_id) return;
    setLoading(true);
    const authHeader = { headers: { authorization: `Bearer ${getToken()}` } };
    Promise.all([
      axiosInstance.get(`${API}/listStudentsInClass/${guidance.r_id}`, authHeader),
      // รายการลาที่ครูประจำชั้นแจ้งไว้สำหรับวันที่เลือก (พังก็ไม่ block หน้า)
      axiosInstance.get(`${API}/getLeavesByRoomDate/${guidance.r_id}/${selectedDate}${guidance.period_id ? `?periodId=${guidance.period_id}` : ""}`, authHeader)
        .then((res) => res.data).catch(() => []),
    ])
      .then(([response, leaves]) => {
        const leaveMap = new Map((leaves || []).map((l) => [l.study_id, l]));
        // คนที่ยามบันทึกว่ากลับเข้าโรงเรียนแล้วไม่นับเป็นลาของคาบนี้
        const onLeave = (student) => !!leaveMap.get(student.study_id)?.is_active;
        const savedData = getSavedAttendance();
        if (savedData) {
          // มี draft อยู่แล้ว: คงสถานะเดิม แต่ refresh badge การลา
          setData(savedData.map((student) => ({
            ...student,
            advisorLeave: leaveMap.get(student.study_id) || null,
          })));
        } else {
          setData(response.data.map((student) => ({
            ...student,
            advisorLeave: leaveMap.get(student.study_id) || null,
            attendanceStatus: (isInactiveStudent(student) || isTransferredStudent(student))
              ? "absent"
              : (onLeave(student) ? "leave" : "present"),
          })));
        }
      })
      .catch((err) => {
        Swal.fire({
          title: err.response?.data?.title || err.response?.statusText || "เกิดข้อผิดพลาด",
          text: err.response?.data?.message || err.message,
          icon: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [setLoading, guidance, selectedDate, attendanceKey, getSavedAttendance]);

  // เก็บ draft ลง localStorage ทุกครั้งที่ data เปลี่ยน
  useEffect(() => {
    if (data && data.length > 0) {
      localStorage.setItem(attendanceKey, JSON.stringify(data));
    }
  }, [data, attendanceKey]);

  // ─── Already checked on selected date? ────────────────────────────────────
  useEffect(() => {
    if (!guid_id || !selectedDate) return;
    axiosInstance.get(`${API}/getGuidanceAttendanceForEdit/${guid_id}/${selectedDate}`, {
      headers: { authorization: `Bearer ${getToken()}` },
    })
      .then((res) => setAlreadyChecked(res.data && res.data.length > 0))
      .catch(() => setAlreadyChecked(false));
  }, [guid_id, selectedDate]);

  const handleAttendanceChange = (index, status) => {
    const updated = [...data];
    updated[index].attendanceStatus = status;
    setData(updated);
  };

  const handleSaveAttendance = () => {
    if (isSaving) return;
    if (!topic) {
      const topicInput = document.getElementById("topicInput");
      topicInput.classList.add("is-invalid");
      topicInput?.focus();
      return;
    }
    setIsSaving(true);
    setLoading(true);
    axiosInstance.post(`${API}/saveGuidanceAttendance`, {
      guid_id,
      date: selectedDate,
      topic,
      students: data
        .filter((student) => !isInactiveStudent(student) && !isTransferredStudent(student))
        .map((student) => ({
          study_id: student.study_id,
          attendanceStatus: student.attendanceStatus,
        })),
    }, {
      headers: { authorization: `Bearer ${getToken()}` },
    })
      .then(() => {
        localStorage.removeItem(attendanceKey);
        Swal.fire({
          title: "บันทึกการเช็คชื่อสำเร็จ",
          icon: "success",
        }).then(() => {
          navigate(`/advisor/guidance-edit/${guid_id}/${selectedDate}`);
        });
      })
      .catch((err) => {
        localStorage.removeItem(attendanceKey);
        Swal.fire({
          title: err.response?.data?.title || err.response?.statusText || "เกิดข้อผิดพลาด",
          text: err.response?.data?.message,
          icon: "error",
        });
      })
      .finally(() => {
        setLoading(false);
        setIsSaving(false);
      });
  };

  const roomLabel = guidance ? `ม.${guidance.tb_class_c_id}/${guidance.r_no}` : "";

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/advisor/guidance">เช็คชื่อคาบแนะแนว</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ชั้น {roomLabel}</li>
            </ol>
            <h4 className="main-title mb-0">เช็คชื่อคาบแนะแนว {roomLabel}</h4>
            {guidance && (
              <div className="text-muted fs-sm mt-1">
                วัน{DAY_NAMES[guidance.teach_day]} คาบ {guidance.period_order}{" "}
                ({formatTimeShort(guidance.period_begin)}–{formatTimeShort(guidance.period_end)})
                {" · "}เทอม {guidance.semester}
              </div>
            )}
          </div>
        </div>

        {alreadyChecked && (
          <Alert variant="warning" className="d-flex align-items-center gap-2 mb-3">
            <i className="ri-error-warning-line fs-5"></i>
            <span>
              คาบแนะแนวเช็คชื่อไปแล้วในวันที่เลือก — หากต้องการแก้ไข{" "}
              <Link to={`/advisor/guidance-edit/${guid_id}/${selectedDate}`} className="text-decoration-underline">กดที่นี่</Link>
            </span>
          </Alert>
        )}

        <Row className="g-12 mb-4">
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mt-md-0">
            {/* เลือกวันที่ */}
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 ps-3" style={{ minWidth: "50px" }}>วันที่:</label>
              <input
                type="date"
                className="form-control"
                style={{ maxWidth: "180px" }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            {/* หัวข้อกิจกรรม */}
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 ps-3" style={{ minWidth: "50px" }}>กิจกรรม:</label>
              <input
                type="text"
                id="topicInput"
                className="form-control"
                style={{ minWidth: "300px", maxWidth: "800px", width: "420px" }}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="ระบุกิจกรรมแนะแนว หรือหัวข้อที่พูดคุยกับนักเรียน"
              />
            </div>
          </div>
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                {/* ปุ่มสลับมุมมอง ตาราง / การ์ด */}
                <div className="d-flex justify-content-end mb-3">
                  <ButtonGroup size="sm">
                    <Button
                      variant={effectiveView === "card" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("card")}
                      title="มุมมองการ์ด"
                    >
                      <i className="ri-layout-grid-line"></i> การ์ด
                    </Button>
                    <Button
                      variant={effectiveView === "table" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("table")}
                      title="มุมมองตาราง"
                    >
                      <i className="ri-table-line"></i> ตาราง
                    </Button>
                  </ButtonGroup>
                </div>

                {effectiveView === "table" ? (
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th scope="col" className="text-center" style={{ width: "50px" }}>เลขที่</th>
                        <th scope="col" className="text-center" style={{ width: "100px" }}>เลขประจำตัว</th>
                        <th scope="col">ชื่อ-สกุล</th>
                        <th scope="col" className="text-center" style={{ width: "150px" }}>สถานะการเข้าเรียน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data && data.length > 0 ? (
                        data.map((student, index) => (
                          <tr key={student.study_id} className={isInactiveStudent(student) ? "table-danger" : isTransferredStudent(student) ? "table-warning" : ""}>
                            <th scope="row" className="text-center">{index + 1}</th>
                            <td className="text-center">{student.st_idnumber}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <StudentAvatar photo={student.st_photo} name={student.st_name} />
                                <span>
                                  {student.st_name}
                                  {isTransferredStudent(student) && (
                                    <small className="text-muted ms-2">(ย้ายไป ม.{student.transfer_class_id}/{student.transfer_room_no})</small>
                                  )}
                                  <LeaveBadge leave={student.advisorLeave} />
                                </span>
                              </div>
                            </td>
                            <td className="text-center">
                              {!isInactiveStudent(student) && !isTransferredStudent(student) && (
                                <AttendanceButtons student={student} index={index} onChange={handleAttendanceChange} />
                              )}
                              {isInactiveStudent(student) && (
                                <span className="text-danger">จำหน่าย</span>
                              )}
                              {isTransferredStudent(student) && (
                                <span className="text-warning">ย้ายห้อง</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={4} className="text-center">ไม่พบข้อมูล</td></tr>
                      )}
                    </tbody>
                  </Table>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {data && data.length > 0 ? (
                      data.map((student, index) => (
                        <Card
                          key={student.study_id}
                          className={`border ${isInactiveStudent(student) ? "border-danger bg-danger bg-opacity-10" : isTransferredStudent(student) ? "border-warning bg-warning bg-opacity-10" : ""}`}
                        >
                          <Card.Body className="p-0" style={{ minHeight: 76 }}>
                            <div className="d-flex" style={{ minHeight: 76 }}>
                              {/* รูปใหญ่ชิดซ้าย */}
                              <div style={{ width: 64, flexShrink: 0, overflow: "hidden", borderRadius: "calc(0.375rem - 1px) 0 0 calc(0.375rem - 1px)" }}>
                                <StudentAvatar photo={student.st_photo} name={student.st_name} fill />
                              </div>
                              {/* ข้อมูล + ปุ่ม */}
                              <div className="flex-grow-1 px-2 py-2 d-flex flex-column justify-content-between">
                                <div className="d-flex align-items-start gap-2">
                                  <span className="badge bg-secondary flex-shrink-0 mt-1">{index + 1}</span>
                                  <div className="flex-grow-1 text-end">
                                    <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>{student.st_name}</div>
                                    <small className="text-muted">เลขประจำตัว {student.st_idnumber}</small>
                                    {isTransferredStudent(student) && (
                                      <div><small className="text-muted">(ย้ายไป ม.{student.transfer_class_id}/{student.transfer_room_no})</small></div>
                                    )}
                                    {student.advisorLeave && <div className="mt-1"><LeaveBadge leave={student.advisorLeave} /></div>}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-end">
                                  {!isInactiveStudent(student) && !isTransferredStudent(student) && (
                                    <AttendanceButtons student={student} index={index} onChange={handleAttendanceChange} />
                                  )}
                                  {isInactiveStudent(student) && (
                                    <span className="text-danger">จำหน่าย</span>
                                  )}
                                  {isTransferredStudent(student) && (
                                    <span className="text-warning">ย้ายห้อง</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center text-muted py-3">ไม่พบข้อมูล</div>
                    )}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-3">
                  <Button variant="success" size="sm" onClick={handleSaveAttendance} disabled={isSaving}>
                    <i className="ri-save-3-fill"> {isSaving ? "กำลังบันทึก..." : "บันทึกการเข้าเรียน"}</i>
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
